import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ListFilter, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useLocation, useRoute } from "wouter";
import CommonGrid from "@/components/pos/admin/common/grid";
import { usePagePersistentState } from "@/hooks/pos/use-page-persisted-state";
import AdvanceFiltersModal, { type FilterConfig } from "@/components/pos/admin/common/advance-filters-modal";
import ActionTemplate from "@/components/pos/admin/common/action-template";
import { useDebounce } from "@/hooks/pos/use-debounce";
import NoRecordFound from "@/components/pos/admin/common/no-record-found";
import ConfirmModal from "@/components/pos/admin/common/confirmation-modal";
import { NameAvatar } from "@/components/pos/admin/common/avatar";
import { useDateFormatter } from "@/hooks/pos/use-date-formatter";
import { useSortableTable } from "@/hooks/pos/use-sortable-table";
import { posApiFetch } from "@/utils/pos-api";
import { getStatusColorCode, getStatusDotColor } from "@/lib/pos/global";
import { drinkTypes } from "@/lib/pos/constants";
import { useToast } from "@/hooks/use-toast";
import CustomDrinkEditor from "@/components/pos/admin/custom-drink-editor";

const fetchDrinkComponents = async () => {
  const res = await posApiFetch("/v1/pos/drink-components");
  if (!res.ok) throw new Error("Failed to fetch drink components");
  return res.json();
};

type CustomDrinksState = {
  type?: string;
  status?: boolean;
  currentPage?: number;
  itemsPerPage?: number;
  searchQuery?: string;
  priceRange?: { min: string; max: string };
};

const defaultCustomDrinksState: CustomDrinksState = {
  type: "all",
  currentPage: 1,
  itemsPerPage: 10,
  priceRange: { min: "", max: "" },
};

export default function CustomDrinksPage() {
  const { toast } = useToast();
  const { formatDate } = useDateFormatter();
  const [, navigate] = useLocation();
  const queryClient = useQueryClient();

  const [isAddRoute] = useRoute("/admin/custom-drinks/add");
  const [isEditRoute, editParams] = useRoute("/admin/custom-drinks/:id/edit");

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("all");

  useEffect(() => { document.title = "Custom Drinks | SECH Admin"; }, []);

  const [customDrinksState, setCustomDrinksState] = usePagePersistentState<CustomDrinksState>("customDrinks", defaultCustomDrinksState);
  const searchQuery = customDrinksState.searchQuery;
  const currentPage = customDrinksState.currentPage;
  const itemsPerPage = customDrinksState.itemsPerPage;
  const statusFilter = customDrinksState.status;
  const typeFilter = customDrinksState.type;
  const priceRange = customDrinksState.priceRange;
  const debouncedSearch = useDebounce(searchQuery?.trim() ?? "", 300);

  const { sortColumn, sortDirection, handleSort } = useSortableTable<string>({
    storageKey: "custom_drinks_table",
    defaultColumn: "name",
    defaultDirection: "asc",
  });

  const [filterValues, setFilterValues] = useState<Record<string, any>>({
    status: "", type: "", priceRange: { min: "", max: "" },
  });

  useEffect(() => {
    setFilterValues((prev) => ({
      ...prev,
      status: statusFilter !== undefined ? (statusFilter ? "active" : "inactive") : "",
      type: typeFilter ?? "",
      priceRange: { min: priceRange?.min ?? "", max: priceRange?.max ?? "" },
    }));
  }, [statusFilter, typeFilter, priceRange]);

  const handleApplyFilters = (values: Record<string, any>) => {
    setFilterValues(values);
    setCustomDrinksState((prev) => ({
      ...prev,
      status: values.status === "active" ? true : values.status === "inactive" ? false : undefined,
      type: values.type || "all",
      currentPage: 1,
      priceRange: { min: values.priceRange?.min ? String(values.priceRange.min) : "", max: values.priceRange?.max ? String(values.priceRange.max) : "" },
    }));
  };

  const { data: drinkComponents = [], isLoading, error } = useQuery({
    queryKey: ["/v1/pos/drink-components"],
    queryFn: fetchDrinkComponents,
    retry: 3,
    retryDelay: 1000,
  });

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      const res = await posApiFetch(`/v1/pos/drink-components/${deleteTarget.id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete drink component");
      toast({ title: "Drink component deleted successfully" });
      queryClient.invalidateQueries({ queryKey: ["/v1/pos/drink-components"] });
    } catch {
      toast({ title: "Error", description: "Failed to delete drink component", variant: "destructive" });
    } finally {
      setDeleteModal(false);
      setDeleteTarget(null);
    }
  };

  const getFilterCount = () => {
    let count = 0;
    if (statusFilter !== undefined) count += 1;
    if (customDrinksState.type && customDrinksState.type !== "all") count += 1;
    const minStr = customDrinksState.priceRange?.min?.toString()?.trim() ?? "";
    const maxStr = customDrinksState.priceRange?.max?.toString()?.trim() ?? "";
    if (minStr !== "" || maxStr !== "") count += 1;
    return count;
  };

  const statusOptions = [
    { label: "All", value: "all" },
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const typeOptions = [
    { label: "All", value: "all" },
    ...drinkTypes.map((t) => ({ label: t.label, value: t.value })),
  ];

  const customDrinkFilters: FilterConfig[] = useMemo(() => [
    { key: "status", label: "Status", type: "select", options: statusOptions, placeholder: "Select Option" },
    { key: "type", label: "Type", type: "select", options: typeOptions, placeholder: "Select Option" },
    { key: "priceRange", label: "Price Range (USD)", type: "numberRange", min: 0, max: 50, step: 1, prefix: "$" },
  ], []);

  const handleClear = () => {
    setFilterValues({ status: "", type: "all", priceRange: { min: "", max: "" } });
    setCustomDrinksState((prev) => ({ ...prev, status: undefined, type: "all", priceRange: { min: "", max: "" }, currentPage: 1 }));
  };

  const getTypeLabel = (type: string) => {
    const found = drinkTypes.find((t) => t.value === type);
    return found ? found.label : type ? type.charAt(0).toUpperCase() + type.slice(1) : "-";
  };

  const setTableColumns = useMemo(() => [
    {
      label: "Name", field: "name", sortable: true,
      cellTemplate: (value: string, row: any) => (
        <div className="flex items-center gap-2">
          <NameAvatar name={value || ""} imageUrl={row?.imageUrl} />
          <span className="text-[13px] text-[#505258]">{value}</span>
        </div>
      ),
    },
    {
      label: "Type", field: "type", sortable: true,
      cellTemplate: (value: string) => <span className="text-[13px] text-[var(--secondary-text-color)]">{getTypeLabel(value)}</span>,
    },
    {
      label: "Price (USD)", field: "priceUsd", sortable: true,
      cellTemplate: (value: any) => <span className="text-[13px] text-[var(--secondary-text-color)]">${parseFloat(value ?? "0").toFixed(2)}</span>,
    },
    {
      label: "Status", field: "available", sortable: true,
      cellTemplate: (value: any) => {
        const status = value ? "active" : "inactive";
        return (
          <span className={`px-2 h-5 flex items-center w-fit rounded-full text-xs font-medium ${getStatusColorCode(status)}`}>
            <span className={`h-2 w-2 rounded-full mr-1 ${getStatusDotColor(status)} inline-flex items-center justify-center`} />
            <span className="leading-5 text-[13px] font-normal">{status.charAt(0).toUpperCase() + status.slice(1)}</span>
          </span>
        );
      },
    },
    {
      label: "Date & Time", field: "createdAt", sortable: true,
      cellTemplate: (value: string) => formatDate(value),
    },
    {
      label: "Action", field: "id",
      headerTemplate: () => <span className="text-[13px]"><span className="h-[28px] w-[28px] flex items-center justify-center">Action</span></span>,
      cellTemplate: (_value: any, row: any) => (
        <ActionTemplate
          row={row}
          actions={[
            { label: "Edit", icon: Pencil, onClick: (r: any) => navigate(`/admin/custom-drinks/${r.id}/edit`) },
            { label: "Delete", icon: Trash2, onClick: (r: any) => { setDeleteTarget(r); setDeleteModal(true); }, destructive: true },
          ]}
        />
      ),
    },
  ], []);

  const filteredDrinkComponents = useMemo(() =>
    drinkComponents.filter((component: any) => {
      const search = debouncedSearch?.trim().toLowerCase() ?? "";
      // Tab filter
      const matchesTab = activeTab === "all" || component.type === activeTab;
      // Status filter from advanced filters
      const matchesStatus = statusFilter === undefined || component.available === statusFilter;
      // Type filter from advanced filters
      const matchesType = typeFilter === "all" || !typeFilter || component.type === typeFilter;
      // Search filter
      const matchesSearch = !search || component.name?.toLowerCase().includes(search) || component.type?.toLowerCase().includes(search);
      // Price range filter
      const minStr = customDrinksState.priceRange?.min ?? "";
      const maxStr = customDrinksState.priceRange?.max ?? "";
      const min = minStr === "" ? null : Number(minStr);
      const max = maxStr === "" ? null : Number(maxStr);
      const price = parseFloat(component.priceUsd ?? "0");
      const matchesPriceRange = (min === null || price >= min) && (max === null || price <= max);
      return matchesTab && matchesStatus && matchesType && matchesSearch && matchesPriceRange;
    }), [drinkComponents, activeTab, statusFilter, typeFilter, debouncedSearch, customDrinksState.priceRange]
  );

  const sortedDrinkComponents = useMemo(() => {
    return [...filteredDrinkComponents].sort((a: any, b: any) => {
      let aVal: any, bVal: any;
      switch (sortColumn) {
        case "name": aVal = a?.name ?? ""; bVal = b?.name ?? ""; break;
        case "type": aVal = a?.type ?? ""; bVal = b?.type ?? ""; break;
        case "priceUsd": aVal = parseFloat(a?.priceUsd ?? "0"); bVal = parseFloat(b?.priceUsd ?? "0"); break;
        case "available": aVal = a?.available ? 1 : 0; bVal = b?.available ? 1 : 0; break;
        case "createdAt": aVal = a?.createdAt ? new Date(a.createdAt).getTime() : 0; bVal = b?.createdAt ? new Date(b.createdAt).getTime() : 0; break;
        default: aVal = a?.name ?? ""; bVal = b?.name ?? "";
      }
      const comparison = typeof aVal === "string" && typeof bVal === "string" ? aVal.localeCompare(bVal) : aVal - bVal;
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredDrinkComponents, sortColumn, sortDirection]);

  const totalPages = Math.ceil(sortedDrinkComponents.length / (itemsPerPage ?? 10));
  const startIndex = ((currentPage ?? 1) - 1) * (itemsPerPage ?? 10);
  const paginatedDrinkComponents = sortedDrinkComponents.slice(startIndex, startIndex + (itemsPerPage ?? 10));

  const handlePageChange = (page: number) => { setCustomDrinksState((prev) => ({ ...prev, currentPage: page })); };
  const handleItemsPerPageChange = (value: string) => { setCustomDrinksState((prev) => ({ ...prev, itemsPerPage: Number(value), currentPage: 1 })); };

  const pagination = { totalPages, currentPage, itemsPerPage, totalItems: sortedDrinkComponents.length, handlePageChange, handleItemsPerPageChange };

  const tabCounts = useMemo(() => {
    const counts: Record<string, number> = { all: drinkComponents.length };
    drinkComponents.forEach((c: any) => {
      if (c.type) counts[c.type] = (counts[c.type] || 0) + 1;
    });
    return counts;
  }, [drinkComponents]);

  // Sub-route handling
  if (isAddRoute) {
    return <CustomDrinkEditor onBack={() => navigate("/admin/custom-drinks")} />;
  }

  if (isEditRoute) {
    return <CustomDrinkEditor drinkComponentId={editParams?.id} onBack={() => navigate("/admin/custom-drinks")} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md"><CardContent className="p-6">
          <h2 className="text-xl font-bold text-red-600 mb-4">Error Loading Custom Drinks</h2>
          <p className="text-gray-600 mb-4">Failed to load drink components</p>
          <Button onClick={() => window.location.reload()} className="w-full">Reload Page</Button>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="page-header flex justify-between items-center h-[48px] px-[16px] border-b border-[var(--layout-border-color)]">
        <p className="page-heading text-[20px] leading-[30px] font-semibold">Custom Drinks</p>
        <div className="search-bar-n-btns flex justify-between gap-[4px] items-center">
          <div className={`flex h-8 items-center px-1 p-2 rounded-sm ${getFilterCount() > 0 ? "bg-[var(--secondary-background-color)]" : ""}`}>
            <Button onClick={() => setIsFilterOpen(true)} variant="ghost" className={`px-2 text-[var(--primary-text-color)] flex items-center text-[13px] font-medium leading-[15px] justify-center gap-2 rounded-[4px] ${getFilterCount() > 0 ? "h-6 hover:bg-[var(--primary-background-hover-color)]" : "h-8"}`}>
              <ListFilter className="h-4 w-4" /> Filter
              {getFilterCount() > 0 && <span className="text-[13px] bg-[var(--primary-color)] h-4 w-4 flex items-center justify-center rounded-full leading-[15px] font-medium text-white">{getFilterCount()}</span>}
            </Button>
            {getFilterCount() > 0 && (
              <>
                <span className="text-[13px] text-[var(--secondary-text-color)] px-1">|</span>
                <Button type="button" onClick={handleClear} className="text-[13px] text-[var(--primary-text-color)] font-medium px-2 h-6 hover:bg-[var(--primary-background-hover-color)] bg-transparent">Clear</Button>
              </>
            )}
          </div>
          <div className="search-bar relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#676879] w-4 h-4" />
            <input className="search h-[32px] pr-[12px] pl-[34px] text-[13px] leading-[20px] font-normal rounded-[4px] border border-[#C3C6D4] placeholder:text-[13px] placeholder:leading-[20px] placeholder:font-normal placeholder:text-[#676879] flex items-center" type="text" placeholder="Search" value={searchQuery ?? ""} onChange={(e) => setCustomDrinksState((prev) => ({ ...prev, searchQuery: e.target.value, currentPage: 1 }))} />
          </div>
          <Button onClick={() => navigate("/admin/custom-drinks/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
            <Plus className="h-4 w-4" /> Add Component
          </Button>
        </div>
      </div>

      <div className="p-[16px]">
        <div className="main">
          <div className="tabs flex items-center gap-[8px] border-b border-[var(--layout-border-color)] pb-[5px]">
            <div
              onClick={() => { setActiveTab("all"); setCustomDrinksState((prev) => ({ ...prev, currentPage: 1 })); }}
              className={`tab cursor-pointer h-[32px] px-[8px] py-[4px] rounded-[4px] flex items-center text-[13px] leading-[16px] font-normal relative ${activeTab === "all" ? "bg-[var(--primary-background-hover-color)]" : ""}`}
            >
              <span className="text text-[var(--secondary-text-color)]">All</span>
              <span className="ml-1 text-[11px] text-[var(--secondary-text-color)]">({tabCounts.all || 0})</span>
              <div className={`absolute bottom-[-6px] left-0 right-0 h-[2px] bg-[var(--primary-color)] rounded-t-full ${activeTab === "all" ? "block" : "hidden"}`} />
            </div>
            {drinkTypes.map((dt) => (
              <div
                key={dt.value}
                onClick={() => { setActiveTab(dt.value); setCustomDrinksState((prev) => ({ ...prev, currentPage: 1 })); }}
                className={`tab cursor-pointer h-[32px] px-[8px] py-[4px] rounded-[4px] flex items-center text-[13px] leading-[16px] font-normal relative ${activeTab === dt.value ? "bg-[var(--primary-background-hover-color)]" : ""}`}
              >
                <span className="text text-[var(--secondary-text-color)]">{dt.label}</span>
                <span className="ml-1 text-[11px] text-[var(--secondary-text-color)]">({tabCounts[dt.value] || 0})</span>
                <div className={`absolute bottom-[-6px] left-0 right-0 h-[2px] bg-[var(--primary-color)] rounded-t-full ${activeTab === dt.value ? "block" : "hidden"}`} />
              </div>
            ))}
          </div>
        </div>
        <div className="pt-3 h-[calc(100vh-170px)]">
          {!paginatedDrinkComponents.length && !isLoading ? (
            <NoRecordFound title="Drink Components" description="There are no drink components available." action={
              <Button onClick={() => navigate("/admin/custom-drinks/add")} className="h-[32px] px-3 text-[13px] bg-[var(--primary-color)] text-white rounded-[4px] hover:bg-[#9f0e0e] flex items-center gap-1">
                <Plus className="h-4 w-4" /> Add Component
              </Button>
            } />
          ) : (
            <CommonGrid
              rowData={paginatedDrinkComponents}
              isLoading={isLoading}
              columns={setTableColumns}
              pagination={pagination}
              isPaginationRequired={true}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSort={handleSort}
            />
          )}
        </div>
      </div>
      <AdvanceFiltersModal isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} title="Filter" filters={customDrinkFilters} initialValues={filterValues} onApply={handleApplyFilters} onClearAll={handleClear} />
      <ConfirmModal open={deleteModal} onClose={() => { setDeleteModal(false); setDeleteTarget(null); }} onConfirm={handleDelete} title="Delete Drink Component" description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`} confirmText="Delete" variant="delete" />
    </div>
  );
}
